import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { usePatientRole } from '@/hooks/usePatientRole';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedImage } from '@/components/ui/signed-image';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { ImageCropModal } from '@/components/ui/image-crop-modal';
import { MapPicker } from '@/components/ui/map-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Camera, User, MapPin, FileText, Calendar, HeartPulse, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '@/lib/image-utils';

export default function EditPatientPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { canEdit, isLoading: roleLoading } = usePatientRole(patientId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [nickname, setNickname] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [healthDetails, setHealthDetails] = useState('');

  // Location states
  const [residenceLatitude, setResidenceLatitude] = useState<number | null>(null);
  const [residenceLongitude, setResidenceLongitude] = useState<number | null>(null);
  // Photo crop state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [croppedPhoto, setCroppedPhoto] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (patientId) {
      fetchPatient();
    }
  }, [patientId]);

  const fetchPatient = async () => {
    if (!patientId) return;

    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('id', patientId)
        .single();

      if (error) throw error;

      if (data) {
        setNickname(data.nickname || '');
        setAddress(data.address || '');
        setNotes(data.notes || '');
        setBirthDate(data.birth_date || '');
        setHealthDetails(data.health_details || '');
        setCurrentPhotoUrl(data.photo_url);
        setResidenceLatitude(data.residence_latitude);
        setResidenceLongitude(data.residence_longitude);
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
      toast.error(t('common.error'));
      navigate('/app/patients');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.selectImage'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // Relaxed since we compress
      toast.error(t('profile.imageMaxSize'));
      return;
    }

    setSelectedFile(file);
    setCropModalOpen(true);
    e.target.value = '';
  };

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const uploadPhoto = async (): Promise<string | null> => {
    if (!croppedPhoto || !patientId) return null;

    const filePath = `${patientId}/photo.jpg`;

    const { error: uploadError } = await supabase.storage
      .from('patient-photos')
      .upload(filePath, croppedPhoto, { upsert: true, contentType: 'image/jpeg' });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    // Store the file path reference - signed URLs will be generated when viewing
    const { data } = supabase.storage
      .from('patient-photos')
      .getPublicUrl(filePath);

    return `${data.publicUrl}?t=${Date.now()}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nickname.trim()) {
      toast.error(t('common.required'));
      return;
    }

    if (!user || !patientId) {
      toast.error(t('common.error'));
      return;
    }

    setIsSaving(true);

    try {
      let photoUrl = currentPhotoUrl;

      if (croppedPhoto) {
        const newPhotoUrl = await uploadPhoto();
        if (newPhotoUrl) {
          photoUrl = newPhotoUrl;
        }
      }

      const { error } = await supabase
        .from('patients')
        .update({
          nickname: nickname.trim(),
          address: address.trim() || null,
          notes: notes.trim() || null,
          birth_date: birthDate || null,
          health_details: healthDetails.trim() || null,
          photo_url: photoUrl,
          residence_latitude: residenceLatitude,
          residence_longitude: residenceLongitude
        })
        .eq('id', patientId);

      if (error) throw error;

      toast.success(t('common.success'));
      navigate(`/app/patients/${patientId}`);
    } catch (error: any) {
      console.error('Error updating patient:', error);
      toast.error(error.message || t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || roleLoading) {
    return (
      <AppLayout
        title={t('common.edit')}
        showBack
        onBack={() => navigate(`/app/patients/${patientId}`)}
        showFAB={false}
      >
        <div className="p-4 space-y-4">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      </AppLayout>
    );
  }

  if (!canEdit) {
    return (
      <AppLayout
        title={t('common.edit')}
        showBack
        onBack={() => navigate(`/app/patients/${patientId}`)}
        showFAB={false}
      >
        <div className="p-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">
                {t('patient.noPermission')}
              </p>
              <Button
                className="mt-4"
                onClick={() => navigate(`/app/patients/${patientId}`)}
              >
                {t('common.back')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t('common.edit')}
      showBack
      onBack={() => navigate(`/app/patients/${patientId}`)}
      showFAB={false}
    >
      <div className="p-4">
        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardContent className="pt-6">
              {/* Photo Upload */}
              <div className="flex flex-col items-center mb-6">
                <div
                  className="relative cursor-pointer group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Avatar className="w-24 h-24">
                    {photoPreview ? (
                      <AvatarImage src={photoPreview} />
                    ) : (
                      <SignedAvatar
                        src={currentPhotoUrl}
                        bucket="patient-photos"
                        className="w-full h-full"
                        fallback={
                          <span className="bg-primary/10 text-primary flex items-center justify-center w-full h-full">
                            <User className="w-10 h-10" />
                          </span>
                        }
                      />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary">
                      <User className="w-10 h-10" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute inset-0 flex items-center justify-center bg-foreground/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="w-8 h-8 text-background" />
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                <p className="text-sm text-muted-foreground mt-2">
                  {t('patient.tapToChangePhoto')}
                </p>
              </div>

              <ImageCropModal
                open={cropModalOpen}
                imageFile={selectedFile}
                onClose={() => {
                  setCropModalOpen(false);
                  setSelectedFile(null);
                }}
                onCrop={async (blob) => {
                  setCropModalOpen(false);
                  setSelectedFile(null);

                  // Compress cropped photo
                  const { blob: finalBlob, previewUrl } = await compressImage(blob, {
                    maxWidth: 512,
                    maxHeight: 512,
                    quality: 0.8
                  });

                  setCroppedPhoto(finalBlob);
                  if (photoPreview?.startsWith('blob:')) {
                    URL.revokeObjectURL(photoPreview);
                  }
                  setPhotoPreview(previewUrl);
                }}
                size={256}
              />

              {/* Nickname */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="nickname" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {t('patient.nickname')} *
                </Label>
                <Input
                  id="nickname"
                  placeholder={t('patient.nickname')}
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  required
                />
              </div>

              {/* Birth Date */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="birthDate" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t('patient.birthDate')}
                </Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              </div>

              {/* Address */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t('patient.address')}
                </Label>
                <Input
                  id="address"
                  placeholder={t('patient.address')}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              {/* Health Details */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="healthDetails" className="flex items-center gap-2">
                  <HeartPulse className="w-4 h-4" />
                  {t('patient.healthDetails')}
                </Label>
                <Textarea
                  id="healthDetails"
                  placeholder={t('patient.healthDetails')}
                  value={healthDetails}
                  onChange={(e) => setHealthDetails(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Notes */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="notes" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t('patient.observations')}
                </Label>
                <Textarea
                  id="notes"
                  placeholder={t('patient.observations')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Residence Location */}
              <div className="flex items-center justify-between mb-4">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t('patient.residenceLocation')}
                </Label>
              </div>

              <div className="space-y-2">
                <MapPicker
                  latitude={residenceLatitude}
                  longitude={residenceLongitude}
                  onLocationSelect={(lat, lng) => {
                    setResidenceLatitude(lat);
                    setResidenceLongitude(lng);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {t('patient.locationHelpText') || "Clique no mapa para ajustar a localização exata da residência."}
                </p>
              </div>
            </CardContent>
          </Card>

          <Button type="submit" className="w-full" disabled={isSaving}>
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </form>
      </div>
    </AppLayout>
  );
}
